import React, { FC, useState } from 'react';
import { View } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import ColorWrapper from '@styles/colors';
import TouchableElement from '@components/common/touchableElement';
import Svg from '@styles/icons/svg';
import ModalWrapper from '@components/modal/ModalWrapper';
import LayeredModal from '@components/modal/LayeredModal';
import ActionMenu from '@components/common/actionMenu';

import { isWeb, JOB_STATUSES, machineDetailsConditions, triggerRemovePI } from '@helpers';
import { scaleOP } from '@components/frame';
import { setActionsModalOpen, setIsRemovingPI } from '@reducers/machinesV2';
import { removeProductionInformation } from '@reducers/manufacture';
import { useContainerPositionRef } from '@v2/pages/common/hooks/useContainerPositionRef';
import { getJobOrOrderStatusConfig, ICON_BUTTON_PADDING } from '@v2/pages/common/constants/base.constants';
import { ColorFunction } from '@styles/colors/types';
import { Machine } from '@interfaces/machines';
import { DecrementTabCount, RemoveProductionInformation } from '@interfaces/methods';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { decrementTabCount } from '@reducers/operatorPanel';
import { OPERATOR_PANEL_INFORMATION_SCREENS } from '@components/operatorPanel/constants/operatorPanel.constants';
import { fontOP } from '@styles/fonts';
import { OperationSearchItem } from '@interfaces/search';
import StartJobPopup from '@components/search/operations/components/StartJobPopup';
import StatusBadge from '@components/common/StatusBadge';

interface OperatorPanelInformationJobsActionsProps {
  C: ColorFunction;
  selectedItem: Machine;
  isRemovingPI: boolean;
  selectedJobId: number | undefined;
  selectedJob: OperationSearchItem | undefined;
  setIsRemovingPI: (state: boolean) => void;
  removeProductionInformation: RemoveProductionInformation;
  scale?: ScaleFunction;
  font?: typeof fontOP;
  showActions: boolean;
  productionBatchId: number | undefined;
  ncFileId: number | undefined;
  decrementTabCount: DecrementTabCount;
}

const OperatorPanelInformationJobsActions: FC<OperatorPanelInformationJobsActionsProps> = (props) => {
  const {
    C,
    selectedItem,
    isRemovingPI,
    selectedJobId,
    selectedJob,
    setIsRemovingPI,
    removeProductionInformation,
    showActions,
    ncFileId,
    productionBatchId,
    decrementTabCount,
    scale = scaleOP,
    font = fontOP,
  } = props;

  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  const [startJobModalOpen, setStartJobModalOpen] = useState(false);

  const [actionMenuContainerRef] = useContainerPositionRef();

  const { status, ncFileStatus, subStatus, ncFile } = selectedItem?.machineStatus || {};

  const { machineActive, ignoredNcFile } = machineDetailsConditions({
    status,
    ncFileStatus,
    subStatus,
    ncFileExist: !!ncFile,
  });

  const onJobRemoveFromBatch = () => {
    if (isRemovingPI || selectedJobId == null || productionBatchId == null || ncFileId == null) {
      return;
    }
    triggerRemovePI({
      params: { productionOperationId: selectedJobId, productionBatchId, ncFileId },
      removeProductionInformation,
      setActionsModalOpen,
      setIsRemovingPI,
      onRemove: () => decrementTabCount({ tabName: OPERATOR_PANEL_INFORMATION_SCREENS.Jobs }),
    });
  };

  const items = [
    {
      lable: {
        color: 'Label/Light/Warning',
        name: i18n.t('Remove Job from Batch'),
      },
      hide: !machineActive && !ignoredNcFile,
      icon: {
        name: 'remove',
        width: isWeb ? 17 : 15,
        height: isWeb ? 18 : 17,
        color: 'Label/Light/Warning',
      },
      isLoading: isRemovingPI,
      onPress: onJobRemoveFromBatch,
    },
  ];

  const handleBackdropPress = () => {
    if (actionsModalOpen) {
      setActionsModalOpen(false);
    }
  };

  return (
    <>
      {selectedJob && selectedJob?.status && (
        <>
          <StatusBadge C={C} config={getJobOrOrderStatusConfig(selectedJob?.status, C)} scale={scale} font={font} />
          {selectedJob.status !== JOB_STATUSES.RUNNING && (
            <TouchableElement
              onPress={() => {
                setStartJobModalOpen(true);
              }}
            >
              {({ boxStyle }) => (
                <View style={[boxStyle]}>
                  <StatusBadge C={C} config={getJobOrOrderStatusConfig(JOB_STATUSES.START, C)} scale={scale} font={font} />
                </View>
              )}
            </TouchableElement>
          )}
        </>
      )}
      {showActions ? (
        <TouchableElement onPress={() => setActionsModalOpen(true)}>
          {({ boxStyle, pressHoverColor }) => (
            <View
              style={[
                boxStyle,
                {
                  backgroundColor: C(pressHoverColor('Button/Light/Only Text')),
                  padding: scale(ICON_BUTTON_PADDING),
                  borderRadius: scale(18),
                },
                isWeb && { outline: 'none' },
              ]}
              ref={actionMenuContainerRef}
            >
              <Svg icon="more" width={scale(22)} height={scale(22)} color={'Button/Light/Primary'} style={{ overflow: 'visible' }} />
            </View>
          )}
        </TouchableElement>
      ) : null}
      <ModalWrapper isVisible={actionsModalOpen} C={C} onBackdropPress={handleBackdropPress}>
        <View
          style={{
            position: 'absolute',
            top: scale(70),
            right: scale(18),
            backgroundColor: C('Background/Light/Field'),
            borderRadius: scale(12),
          }}
        >
          <ActionMenu {...{ C, items }} scale={scale} font={font} />
        </View>
      </ModalWrapper>

      {/* selectedJob can momentarily be undefined while the jobs screen resets on navigation (e.g. Go To Job Screen) — never mount the modal without a job. */}
      {selectedJob && (
        <LayeredModal
          isVisible={startJobModalOpen}
          onBackdropPress={() => setStartJobModalOpen(false)}
          C={C}
          scale={scale}
          contentStyle={{ width: scale(730) }}
        >
          <StartJobPopup hide={() => setStartJobModalOpen(false)} item={selectedJob} scale={scale} font={font} C={C} />
        </LayeredModal>
      )}
    </>
  );
};

const mapStateToProps = ({ machines: { selectedItem }, machinesV2: { actionsModalOpen, isRemovingPI } }) => ({
  isRemovingPI,
  actionsModalOpen,
  selectedItem,
});

export default connect(mapStateToProps, { setIsRemovingPI, removeProductionInformation, setActionsModalOpen, decrementTabCount })(
  ColorWrapper(OperatorPanelInformationJobsActions)
);
